import { useCallback, useEffect, useMemo, useState } from "react";
import { api, eur, downloadBlob } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ScrollText, CreditCard, RotateCcw, ClipboardList, DoorClosed,
  PlayCircle, StopCircle, Hand, Banknote, ShoppingBag, RefreshCw, User,
  Search, CalendarDays, ChevronRight, X, ClipboardCheck, LayoutGrid, UserSearch,
} from "lucide-react";

const EVENT_META = {
  order_created: { icon: ClipboardList, color: "#8FB9A8" },
  order_created_manual: { icon: ClipboardList, color: "#C69C4A" },
  order_created_takeaway: { icon: ShoppingBag, color: "#C69C4A" },
  order_status_changed: { icon: ClipboardList, color: "#8FB9A8" },
  service_request_claimed: { icon: Hand, color: "#8FB9A8" },
  service_started: { icon: PlayCircle, color: "#34D399" },
  service_closed: { icon: StopCircle, color: "#F59E0B" },
  payment_settled: { icon: CreditCard, color: "#34D399" },
  cash_payment: { icon: Banknote, color: "#34D399" },
  payment_refunded: { icon: RotateCcw, color: "#F87171" },
  table_closed: { icon: DoorClosed, color: "#8FB9A8" },
  activity_reset: { icon: RefreshCw, color: "#F87171" },
};
const ROLE_LABEL = { owner: "Propriétaire", manager: "Manager", server: "Serveur", kitchen: "Cuisine", client: "Client" };
const METHOD_LABEL = { cash: "Espèces", card: "CB", ticket: "Ticket restaurant" };
const NA = "Information non disponible";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDateFull(iso) {
  if (!iso) return NA;
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function orderRef(e) { return e.order_code || e.order_number || null; }
function fmtDelay(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  return `${Math.round(s / 60)} min`;
}

function detailOf(e) {
  const bits = [];
  if (e.table_number) bits.push(`Table ${e.table_number}`);
  if (e.product_name) bits.push(e.product_name);
  if (e.name && !e.product_name) bits.push(e.name);
  if (typeof e.remaining === "number") bits.push(`reste ${e.remaining}`);
  if (orderRef(e)) bits.push(orderRef(e));
  if (typeof e.amount === "number" && e.amount > 0) bits.push(eur(e.amount));
  if (e.cash_method) bits.push(METHOD_LABEL[e.cash_method] || String(e.cash_method));
  if (e.refund_status) bits.push(e.refund_status === "full" ? "remboursement total" : "remboursement partiel");
  if (e.forced) bits.push("clôture forcée");
  if (e.reason || e.close_reason) bits.push(`motif : ${e.reason || e.close_reason}`);
  return bits.join(" · ");
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[#132420] last:border-0">
      <span className="text-xs uppercase tracking-wide text-stone-500 shrink-0">{label}</span>
      <span className="text-sm text-stone-100 text-right">{value}</span>
    </div>
  );
}

export default function ActivityLog({ slug, onNavigate }) {
  const [data, setData] = useState({ entries: [], staff: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState("");
  const [event, setEvent] = useState("");
  const [period, setPeriod] = useState("today"); // today | 7d | 30d | all | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [journal, setJournal] = useState(null);
  const [services, setServices] = useState([]);
  const [sumOpen, setSumOpen] = useState(false);
  const [sum, setSum] = useState(null);
  useEffect(() => { if (slug) api.listServices(slug).then(setServices).catch(() => setServices([])); }, [slug]);
  const openServiceSummary = async (id) => { setSumOpen(true); setSum(null); try { setSum(await api.serviceSummary(slug, id)); } catch { setSumOpen(false); } };
  const fmtS = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    const params = {};
    if (employee) params.employee = employee;
    if (event) params.event = event;
    if (period === "custom") {
      if (customStart) { const d = new Date(customStart); d.setHours(0, 0, 0, 0); params.from = d.toISOString(); }
      if (customEnd) { const d = new Date(customEnd); d.setHours(23, 59, 59, 999); params.to = d.toISOString(); }
    } else if (period !== "all") {
      const now = new Date();
      let from;
      if (period === "today") { from = new Date(now); from.setHours(0, 0, 0, 0); }
      else if (period === "7d") { from = new Date(now.getTime() - 7 * 864e5); }
      else if (period === "30d") { from = new Date(now.getTime() - 30 * 864e5); }
      if (from) params.from = from.toISOString();
    }
    api.serviceRequestJournal(slug, params).then(setJournal).catch(() => setJournal(null));
    api.getActivity(slug, params).then(setData).catch(() => setData({ entries: [], staff: [], events: [] })).finally(() => setLoading(false));
  }, [slug, employee, event, period, customStart, customEnd]);

  // Auto-reload for shortcuts; custom period only on "Appliquer".
  useEffect(() => { if (period !== "custom") load(); /* eslint-disable-next-line */ }, [slug, employee, event, period]);

  // Client-side search over the (real) loaded entries.
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.entries;
    return data.entries.filter((e) => {
      const hay = [
        e.label, e.actor_name, e.actor_role && (ROLE_LABEL[e.actor_role] || e.actor_role),
        e.table_number && `table ${e.table_number}`, orderRef(e),
        typeof e.amount === "number" ? String(e.amount) : "", typeof e.amount === "number" ? eur(e.amount) : "",
        e.cash_method && (METHOD_LABEL[e.cash_method] || e.cash_method),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [data.entries, q]);

  const seeEmployeeActivity = (email) => { setEmployee(email || ""); setSelected(null); };

  return (
    <div className="space-y-5" data-testid="activity-log">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#1A3D32] grid place-items-center shrink-0"><ScrollText className="w-5 h-5 text-[#C69C4A]" /></div>
        <div>
          <h2 className="font-serif-display text-2xl">Journal d'activité</h2>
          <p className="text-sm text-stone-400">Traçabilité en lecture seule (données réelles). Cliquez une ligne pour le détail.</p>
        </div>
        <button data-testid="activity-refresh" onClick={load} className="ml-auto flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-100 rounded-full border border-[#1B3028] px-3 py-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher (CMD-2026-…, Table 12, employé, montant, action)"
          className="w-full h-11 rounded-xl bg-[#111F1A] border border-[#1B3028] pl-10 pr-9 text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#C69C4A]" />
        {q && <button data-testid="activity-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"><X className="w-4 h-4" /></button>}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select data-testid="activity-filter-employee" value={employee} onChange={(e) => setEmployee(e.target.value)}
          className="bg-[#111F1A] border border-[#1B3028] rounded-xl px-3 py-2 text-sm text-stone-100">
          <option value="">Tous les employés</option>
          {data.staff.map((s) => <option key={s.email} value={s.email}>{s.name}{s.role ? ` · ${ROLE_LABEL[s.role] || s.role}` : ""}</option>)}
        </select>
        <select data-testid="activity-filter-event" value={event} onChange={(e) => setEvent(e.target.value)}
          className="bg-[#111F1A] border border-[#1B3028] rounded-xl px-3 py-2 text-sm text-stone-100">
          <option value="">Toutes les actions</option>
          {data.events.map((ev) => <option key={ev.key} value={ev.key}>{ev.label}</option>)}
        </select>
        <div className="flex gap-1 bg-[#111F1A] border border-[#1B3028] rounded-xl p-1">
          {[["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["all", "Tout"]].map(([k, l]) => (
            <button key={k} data-testid={`activity-period-${k}`} onClick={() => setPeriod(k)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${period === k ? "bg-[#1A3D32] text-white" : "text-stone-400 hover:text-stone-100"}`}>{l}</button>
          ))}
          <button data-testid="activity-period-custom" onClick={() => setPeriod("custom")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 ${period === "custom" ? "bg-[#1A3D32] text-white" : "text-stone-400 hover:text-stone-100"}`}>
            <CalendarDays className="w-3.5 h-3.5" /> Personnalisée
          </button>
        </div>
      </div>

      {period === "custom" && (
        <div data-testid="activity-custom-range" className="flex flex-wrap items-end gap-3 rounded-xl bg-[#0A1712] border border-[#1B3028] p-3">
          <label className="text-xs text-stone-400">Date de début
            <input data-testid="activity-date-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              className="mt-1 block bg-[#111F1A] border border-[#1B3028] rounded-lg px-2.5 py-1.5 text-sm text-stone-100" />
          </label>
          <label className="text-xs text-stone-400">Date de fin
            <input data-testid="activity-date-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              className="mt-1 block bg-[#111F1A] border border-[#1B3028] rounded-lg px-2.5 py-1.5 text-sm text-stone-100" />
          </label>
          <button data-testid="activity-apply-custom" onClick={load} disabled={!customStart && !customEnd}
            className="rounded-lg bg-[#C69C4A] text-[#0C1713] font-semibold text-sm px-4 py-2 disabled:opacity-40">Appliquer</button>
          <p className="text-[11px] text-stone-500">Astuce : même date en début et fin = une seule journée.</p>
        </div>
      )}

      {journal && (journal.total > 0 || journal.served_count > 0) && (
        <div data-testid="claims-journal" className="rounded-2xl bg-[#0A1712] border border-[#1B3028] p-3">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2 flex items-center gap-1.5"><Hand className="w-3.5 h-3.5 text-[#C69C4A]" /> Prise en charge des demandes</p>
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            {journal.total > 0 && <span className="rounded-full bg-[#111F1A] border border-[#1B3028] px-2.5 py-1 text-stone-300">{journal.claimed}/{journal.total} prises en charge</span>}
            {journal.total > 0 && <span className={`rounded-full px-2.5 py-1 border ${journal.unclaimed_rate > 0 ? "text-amber-400 border-amber-500/40" : "text-emerald-400 border-emerald-500/30"}`}>{journal.unclaimed_rate}% non prises</span>}
            {journal.served_count > 0 && <span data-testid="ready-to-served-avg" className="rounded-full bg-[#111F1A] border border-[#1B3028] px-2.5 py-1 text-[#C69C4A]">Prêt→servi moy. {fmtDelay(journal.avg_ready_to_served_seconds)} · {journal.served_count} plats</span>}
          </div>
          {journal.staff.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
              {journal.staff.map((s) => (
                <div key={s.employee} data-testid={`claims-staff-${s.employee}`} className="flex items-center justify-between rounded-xl bg-[#111F1A] border border-[#1B3028] px-3 py-2">
                  <span className="text-sm text-stone-100 truncate">{s.employee}</span>
                  <span className="text-[11px] text-stone-400 shrink-0">{s.count} · moy. {fmtDelay(s.avg_delay_seconds)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
            {journal.rows.slice(0, 60).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs rounded-lg bg-[#111F1A] border border-[#1B3028] px-3 py-1.5">
                <span className="text-stone-300 truncate">Table {r.table_number} · {r.label || r.type}</span>
                <span className="shrink-0">{r.claimed_by ? <span className="text-stone-400">{r.claimed_by} · <span className="text-[#C69C4A]">{fmtDelay(r.delay_seconds)}</span></span> : <span className="text-amber-400">non prise</span>}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {services.length > 0 && (
        <div data-testid="services-history" className="rounded-2xl bg-[#0A1712] border border-[#1B3028] p-3">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2 flex items-center gap-1.5"><StopCircle className="w-3.5 h-3.5 text-[#C69C4A]" /> Historique des services</p>
          <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto">
            {services.map((s) => (
              <button key={s.id} data-testid={`service-hist-${s.id}`} onClick={() => openServiceSummary(s.id)}
                className="flex items-center justify-between text-left rounded-xl bg-[#111F1A] border border-[#1B3028] px-3 py-2 hover:border-[#C69C4A]/50">
                <span className="text-sm text-stone-100">{fmtS(s.started_at)} → {s.closed_at ? fmtS(s.closed_at) : "en cours"}</span>
                <span className={`text-[11px] font-semibold ${s.status === "OPEN" ? "text-emerald-400" : "text-stone-400"}`}>{s.status === "OPEN" ? "En cours" : "Clôturé"} · Voir</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-2xl bg-[#0A1712] border border-[#1B3028] divide-y divide-[#132420]">
        {loading ? (
          <div className="p-8 text-center text-stone-500 text-sm">Chargement…</div>
        ) : shown.length === 0 ? (
          <div data-testid="activity-empty" className="p-10 text-center text-stone-500 text-sm">Aucune action pour ces critères.</div>
        ) : (
          shown.map((e, i) => {
            const meta = EVENT_META[e.event] || { icon: ScrollText, color: "#8FB9A8" };
            const Icon = meta.icon;
            const detail = detailOf(e);
            return (
              <button key={e.id || i} data-testid="activity-entry" onClick={() => setSelected(e)}
                className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors">
                <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${meta.color}20` }}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-stone-100">{e.label}</span>
                    {detail && <span className="text-xs text-stone-400">{detail}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 text-xs text-stone-500">
                    <User className="w-3 h-3" />
                    <span className="text-stone-300">{e.actor_name}</span>
                    {e.actor_role && <span className="text-[#C69C4A]">· {ROLE_LABEL[e.actor_role] || e.actor_role}</span>}
                  </div>
                </div>
                <span className="text-xs text-stone-500 shrink-0 whitespace-nowrap">{fmtDate(e.at)}</span>
                <ChevronRight className="w-4 h-4 text-stone-600 shrink-0" />
              </button>
            );
          })
        )}
      </div>
      {shown.length > 0 && <p className="text-xs text-stone-600 text-center">{shown.length} action(s) affichée(s){q ? ` · filtre « ${q} »` : ""}</p>}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="activity-detail-modal" className="max-w-md bg-[#0C1713] border border-[#1B3028] text-stone-100">
          {selected && (() => {
            const meta = EVENT_META[selected.event] || { icon: ScrollText, color: "#8FB9A8" };
            const Icon = meta.icon;
            const oref = orderRef(selected);
            const hasAmount = typeof selected.amount === "number" && selected.amount > 0;
            return (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: `${meta.color}20` }}><Icon className="w-5 h-5" style={{ color: meta.color }} /></div>
                  <div>
                    <DialogTitle className="text-lg text-stone-100">{selected.label}</DialogTitle>
                    <DialogDescription className="text-stone-500 text-xs">Détail de l'activité · données réelles</DialogDescription>
                  </div>
                </div>
                <div className="mt-3">
                  <DetailRow label="Date & heure" value={fmtDateFull(selected.at)} />
                  <DetailRow label="Employé" value={selected.actor_name || NA} />
                  <DetailRow label="Rôle" value={selected.actor_role ? (ROLE_LABEL[selected.actor_role] || selected.actor_role) : NA} />
                  <DetailRow label="Table" value={selected.table_number ? `Table ${selected.table_number}` : NA} />
                  <DetailRow label="Commande" value={oref || NA} />
                  <DetailRow label="Montant" value={hasAmount ? eur(selected.amount) : NA} />
                  <DetailRow label="Mode" value={selected.cash_method ? (METHOD_LABEL[selected.cash_method] || selected.cash_method) : NA} />
                  {selected.refund_status && <DetailRow label="Remboursement" value={selected.refund_status === "full" ? "Total" : "Partiel"} />}
                  {(selected.reason || selected.close_reason) && <DetailRow label="Motif" value={selected.reason || selected.close_reason} />}
                  {"forced" in selected && selected.forced && <DetailRow label="Clôture" value="Forcée" />}
                  <DetailRow label="Statut" value="Réussi" />
                </div>
                {/* Navigation vers l'objet concerné */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {oref && onNavigate && (
                    <button data-testid="activity-goto-order" onClick={() => { onNavigate("orders"); setSelected(null); }} className="flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#1A3D32] text-stone-100 px-3 py-2 hover:bg-[#123026]">
                      <ClipboardCheck className="w-3.5 h-3.5" /> Voir la commande
                    </button>
                  )}
                  {selected.table_number && onNavigate && (
                    <button data-testid="activity-goto-table" onClick={() => { onNavigate("tables"); setSelected(null); }} className="flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#1A3D32] text-stone-100 px-3 py-2 hover:bg-[#123026]">
                      <LayoutGrid className="w-3.5 h-3.5" /> Voir la table
                    </button>
                  )}
                  {selected.payment_id && onNavigate && (
                    <button data-testid="activity-goto-payment" onClick={() => { onNavigate("payments"); setSelected(null); }} className="flex items-center gap-1.5 text-xs font-semibold rounded-full bg-[#1A3D32] text-stone-100 px-3 py-2 hover:bg-[#123026]">
                      <CreditCard className="w-3.5 h-3.5" /> Voir le paiement
                    </button>
                  )}
                  {selected.user && (
                    <button data-testid="activity-goto-employee" onClick={() => seeEmployeeActivity(selected.user)} className="flex items-center gap-1.5 text-xs font-semibold rounded-full border border-[#C69C4A]/50 text-[#C69C4A] px-3 py-2 hover:bg-[#C69C4A]/10">
                      <UserSearch className="w-3.5 h-3.5" /> Activités de cet employé
                    </button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={sumOpen} onOpenChange={(o) => !o && setSumOpen(false)}>
        <DialogContent data-testid="service-hist-summary" className="max-w-md bg-[#0C1713] border border-[#1B3028] text-stone-100">
          <DialogTitle className="text-lg flex items-center gap-2"><ScrollText className="w-5 h-5 text-[#C69C4A]" /> Résumé du service</DialogTitle>
          <DialogDescription className="text-stone-500 text-xs">{sum ? (sum.window.closed ? "Clôturé" : "En cours") : "Chargement…"}</DialogDescription>
          {sum && (
            <div id="service-hist-print" className="text-sm mt-2">
              {[["Période", `${fmtS(sum.window.start)} → ${sum.window.closed ? fmtS(sum.window.end) : "en cours"}`],
                ["CA", eur(sum.revenue)],
                ["Couverts", sum.covers.complete ? sum.covers.total : `${sum.covers.total} (incomplet)`],
                ["Ticket / couvert", sum.avg_ticket_per_cover != null ? eur(sum.avg_ticket_per_cover) : "—"],
                ["Tables servies", sum.tables_served], ["Commandes", sum.order_count],
                ["Upsells", `${sum.upsells.count} · ${eur(sum.upsells.value)}`], ["Impayés", eur(sum.unpaid_amount)]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[#132420] py-1.5"><span className="text-stone-400">{k}</span><span className="text-stone-100">{v}</span></div>
              ))}
              <div className="mt-3 flex gap-2">
                <button data-testid="service-hist-print-btn" onClick={() => window.print()} className="flex-1 h-11 rounded-full bg-[#C69C4A] text-[#0C1713] font-bold">Imprimer</button>
                <button data-testid="service-hist-pdf-btn" onClick={async () => { try { const b = await api.serviceSummaryPdf(slug, sum.service?.id); downloadBlob(b, "resume-service.pdf"); } catch { /* noop */ } }} className="flex-1 h-11 rounded-full bg-[#1A3D32] text-stone-100 font-semibold">PDF</button>
                <button data-testid="service-hist-csv-btn" onClick={() => { const s = sum; const rows = [["Champ", "Valeur"], ["Periode", `${fmtS(s.window.start)} - ${s.window.closed ? fmtS(s.window.end) : "en cours"}`], ["CA", s.revenue], ["Couverts", s.covers.total], ["Couverts_complet", s.covers.complete ? "oui" : "non"], ["Ticket_par_couvert", s.avg_ticket_per_cover ?? ""], ["Tables_servies", s.tables_served], ["Commandes", s.order_count], ["Upsells_nb", s.upsells.count], ["Upsells_valeur", s.upsells.value], ["Impayes", s.unpaid_amount]]; downloadBlob(new Blob([rows.map((r) => r.join(";")).join("\n")], { type: "text/csv;charset=utf-8" }), "resume-service.csv"); }} className="flex-1 h-11 rounded-full bg-[#1A3D32] text-stone-100 font-semibold">CSV</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
